// Caixa de entrada: feedback que os apps mandam sobre execuções (POST /v1/feedback). Cada item
// mostra a entrada, o que o modelo respondeu e a correção, e pode virar caso de teste.
import React, { useMemo, useState } from "react";
import { Link } from "wouter";
import { AlertTriangle, Check, Inbox, MessageSquare, PencilLine, ThumbsDown, ThumbsUp } from "lucide-react";
import { api, invalidate, timeAgo, toast, toastError, useQuery } from "../lib";
import { Badge, Button, Empty, Field, Modal, OutputView, Seg } from "../ui";
import { DiffView } from "../editor";

type Status = "open" | "case" | "dismissed";

const KIND: Record<string, { label: string; tone: "amber" | "blue" | "red" | "plain"; icon: React.ReactNode }> = {
  correction: { label: "correção", tone: "amber", icon: <PencilLine size={12} /> },
  rating: { label: "nota", tone: "blue", icon: <ThumbsUp size={12} /> },
  flag: { label: "alerta", tone: "red", icon: <AlertTriangle size={12} /> },
  comment: { label: "comentário", tone: "plain", icon: <MessageSquare size={12} /> },
};

const isObj = (v: any) => v && typeof v === "object" && !Array.isArray(v);
const asText = (v: any) => (v === undefined || v === null ? "" : typeof v === "string" ? v : JSON.stringify(v, null, 2));

export function FeedbackInbox({ p, project }: { p: string; project: any }) {
  const [status, setStatus] = useState<Status>("open");
  const { data } = useQuery<any[]>(`/api/projects/${p}/feedback?status=${status}`, { poll: 5000 });
  const [caseFor, setCaseFor] = useState<any | null>(null);
  const names: Record<string, string> = Object.fromEntries((project?.targets ?? []).map((t: any) => [t.slug, t.name]));
  const refresh = () => {
    invalidate(`/api/projects/${p}/feedback?status=open`);
    invalidate(`/api/projects/${p}/feedback?status=case`);
    invalidate(`/api/projects/${p}/feedback?status=dismissed`);
    invalidate(`/api/projects/${p}`);
  };
  const dismiss = async (f: any) => {
    try {
      await api.post(`/api/feedback/${f.id}/dismiss`);
      toast("Feedback descartado");
      refresh();
    } catch (e) {
      toastError(e);
    }
  };
  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Caixa de entrada</h1>
          <div className="page-sub">Correções e notas que os apps mandam sobre execuções. Cada item pode virar caso de teste, com a entrada da execução e o resultado esperado, e passa a ser checado em toda avaliação.</div>
        </div>
        <Seg value={status} onChange={setStatus} options={[{ value: "open", label: "Abertos" }, { value: "case", label: "Viraram caso" }, { value: "dismissed", label: "Descartados" }]} />
      </div>
      {data && !data.length && (
        <div className="card">
          <Empty icon={<Inbox size={18} />} title={status === "open" ? "Nada novo" : status === "case" ? "Nenhum feedback virou caso ainda" : "Nenhum descartado"}>
            {status === "open" ? (
              <>Os apps mandam feedback com <span className="mono">POST /v1/feedback</span>, passando o <span className="mono">id</span> da execução, o tipo (correção, nota, alerta ou comentário) e, numa correção, o resultado esperado.</>
            ) : null}
          </Empty>
        </div>
      )}
      <div className="col" style={{ gap: 14 }}>
        {data?.map((f) => <FeedbackCard key={f.id} p={p} f={f} name={names[f.target] ?? f.target} onCase={() => setCaseFor(f)} onDismiss={() => dismiss(f)} />)}
      </div>
      {caseFor && <CaseModal p={p} f={caseFor} project={project} onClose={() => setCaseFor(null)} onDone={refresh} />}
    </>
  );
}

function FeedbackCard({ p, f, name, onCase, onDismiss }: { p: string; f: any; name: string; onCase: () => void; onDismiss: () => void }) {
  const ex = f.execution;
  const k = KIND[f.kind] ?? KIND.comment;
  const ratingIcon = f.kind === "rating" ? (Number(f.value) > 0 ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />) : k.icon;
  return (
    <div className="card">
      <div className="card-head">
        <span className="row wrap" style={{ gap: 8 }}>
          <Link href={`/p/${p}/t/${f.target}`} className="card-title">{name}</Link>
          <Badge tone={k.tone}><span className="row" style={{ gap: 4 }}>{ratingIcon}{k.label}</span></Badge>
          {f.app && <span className="chip">{f.app}</span>}
          {ex && !ex.missing && <span className="chip mono">{ex.version ? "v" + ex.version + (ex.channel ? " · " + ex.channel : "") : "rascunho"}</span>}
          <span className="faint" style={{ fontSize: 12 }}>{timeAgo(f.createdAt)}</span>
        </span>
        <span className="row" style={{ gap: 6 }}>
          {f.status === "open" && (
            <>
              <Button size="sm" variant="ghost" onClick={onDismiss}>Descartar</Button>
              <Button size="sm" variant="primary" icon={<Check size={13} />} onClick={onCase} disabled={ex?.missing}>Virar caso</Button>
            </>
          )}
          {f.status === "case" && f.caseRef && (
            <Link href={`/p/${p}/t/${f.target}/avaliacoes`} className="chip">caso {f.caseRef}</Link>
          )}
          {f.status === "dismissed" && <span className="chip">descartado</span>}
        </span>
      </div>
      <div className="card-body fb-grid">
        <div className="col" style={{ gap: 10, minWidth: 0 }}>
          <div className="label">Entrada</div>
          {ex?.missing ? <span className="faint">A execução {ex.id} não existe mais.</span> : <Inputs inputs={ex.inputs} />}
        </div>
        <div className="col" style={{ gap: 10, minWidth: 0 }}>
          {f.kind === "correction" && !ex?.missing ? (
            <Correction output={ex.output} expected={f.expected} format={ex.outputFormat} />
          ) : (
            <>
              <div className="label">Saída do modelo</div>
              {ex && !ex.missing && <OutputView format={ex.outputFormat} value={ex.output} />}
              {f.value !== null && f.value !== undefined && f.kind !== "rating" && <div className="mono faint" style={{ fontSize: 12 }}>valor: {asText(f.value)}</div>}
            </>
          )}
          {f.note && <div className="fb-note">{f.note}</div>}
        </div>
      </div>
    </div>
  );
}

function Inputs({ inputs }: { inputs: Record<string, any> }) {
  return (
    <div className="col" style={{ gap: 8 }}>
      {Object.entries(inputs ?? {}).map(([k, v]) => (
        <div key={k}>
          <div className="var-name" style={{ marginBottom: 3 }}>{k}</div>
          {typeof v === "string" && (/^\/files\//.test(v) || v.startsWith("data:image")) ? (
            <img src={v} style={{ maxWidth: 220, maxHeight: 180, borderRadius: 8, border: "1px solid var(--border)" }} />
          ) : (
            <div className="text-out fb-input">{asText(v)}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// JSON: campo a campo, destacando o que mudou. Texto: diff.
function Correction({ output, expected, format }: { output: any; expected: any; format: string }) {
  if (isObj(output) && isObj(expected)) {
    const keys = [...new Set([...Object.keys(output), ...Object.keys(expected)])];
    return (
      <>
        <div className="label">Correção</div>
        <table className="table fb-fields">
          <thead><tr><th>Campo</th><th>Modelo</th><th>Correção</th></tr></thead>
          <tbody>
            {keys.map((key) => {
              const a = asText(output[key]);
              const b = key in expected ? asText(expected[key]) : a;
              const changed = key in expected && a !== b;
              return (
                <tr key={key} className={changed ? "changed" : undefined}>
                  <td className="var-name">{key}</td>
                  <td className={changed ? "fb-old" : undefined}>{a || <span className="faint">—</span>}</td>
                  <td className={changed ? "fb-new" : "faint"}>{changed ? b : "igual"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </>
    );
  }
  if (format === "text" || typeof expected === "string") {
    return (
      <>
        <div className="label">Correção</div>
        <DiffView a={asText(output).replace(/\n*$/, "\n")} b={asText(expected).replace(/\n*$/, "\n")} />
      </>
    );
  }
  return (
    <>
      <div className="label">Saída do modelo</div>
      <OutputView format={format} value={output} />
      <div className="label">Correção</div>
      <pre className="pre">{asText(expected)}</pre>
    </>
  );
}

function CaseModal({ p, f, project, onClose, onDone }: { p: string; f: any; project: any; onClose: () => void; onDone: () => void }) {
  const target = (project?.targets ?? []).find((t: any) => t.slug === f.target);
  const start = useMemo(() => asText(f.expected ?? (f.kind === "rating" && Number(f.value) > 0 ? f.execution.output : undefined)), [f]);
  const [expected, setExpected] = useState(start);
  const [busy, setBusy] = useState(false);
  const json = isObj(f.execution.output) || isObj(f.expected);
  const submit = async () => {
    let value: any = expected;
    if (json && expected.trim()) {
      try {
        value = JSON.parse(expected);
      } catch {
        toastError(new Error("O esperado não é um JSON válido"));
        return;
      }
    }
    setBusy(true);
    try {
      const r = await api.post(`/api/feedback/${f.id}/case`, { expected: expected.trim() ? value : undefined });
      toast("Caso adicionado", `${r.target}/${r.case.id}: entra na próxima avaliação`);
      onDone();
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} width={620} title="Transformar este feedback em caso de teste" footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button variant="primary" loading={busy} onClick={submit}>Adicionar caso</Button></>}>
      <div className="muted">A entrada da execução vira um caso de teste de <b>{target?.name ?? f.target}</b>, com o resultado esperado abaixo. Assim o erro que o app viu passa a ser checado em toda avaliação, e a checagem de deploy leva ele em conta.</div>
      <Field label="Resultado esperado" hint={json ? "JSON. Os avaliadores de campos comparam só os campos que estiverem aqui." : "Texto que a resposta deveria ter."}>
        <textarea className="input mono" rows={Math.min(14, Math.max(4, expected.split("\n").length + 1))} value={expected} onChange={(e) => setExpected(e.target.value)} style={{ fontSize: 12.5 }} />
      </Field>
    </Modal>
  );
}
